import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';

const G = '#004D35';
const DARK = '#0a1a0f';
const BG = '#f5f3ef';
const BORDER = '#e8e5e0';
const RED = '#c0392b';
const RED_BG = '#fdf2f2';

function LogoFull() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 30, height: 30, background: G, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 40 40" fill="none">
          <path d="M10 11 L20 28 L30 11" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="28" r="2.5" fill="#a8f0c6" />
        </svg>
      </div>
      <span style={{ fontSize: 16, fontWeight: 700, color: G, fontFamily: 'Georgia, serif' }}>Viysr</span>
    </div>
  );
}

export default function TestimonialSubmit() {
  const { token } = useParams();
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ author_name: '', company_name: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  const SITEKEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    fetch(`/api/testimonials/submit/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setNotFound(true); }
        else { setAdvisor(data); }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Load Turnstile script and render widget
  useEffect(() => {
    if (!SITEKEY || !advisor || submitted) return;

    const existing = document.getElementById('cf-turnstile-script');
    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile) {
        if (turnstileWidgetId.current) {
          try { window.turnstile.remove(turnstileWidgetId.current); } catch (e) {}
        }
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: SITEKEY,
          callback: (t) => setTurnstileToken(t),
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };

    if (existing && window.turnstile) {
      renderWidget();
    } else if (!existing) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v1/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if (window.turnstile) { clearInterval(poll); renderWidget(); }
      }, 200);
      return () => clearInterval(poll);
    }
  }, [advisor, SITEKEY, submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.text.trim()) {
      setError('Please fill in your name and testimonial.');
      return;
    }
    if (SITEKEY && !turnstileToken) {
      setError('Please complete the CAPTCHA check.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/testimonials/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, turnstileToken }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Submission failed. Please try again.'); return; }
      setSubmitted(true);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 14, color: '#aaa', fontFamily: 'sans-serif' }}>Loading…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <LogoFull />
          <div style={{ marginTop: 32, fontSize: 20, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif' }}>Link not found</div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#888', fontFamily: 'sans-serif', lineHeight: 1.6 }}>
            This testimonial link may have expired or is no longer available.
          </div>
          <Link to="/" style={{ display: 'inline-block', marginTop: 24, color: G, fontSize: 14, fontFamily: 'sans-serif', fontWeight: 600, textDecoration: 'none' }}>← Back to Viysr</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <LogoFull />
          <div style={{ marginTop: 32, fontSize: 48 }}>🎉</div>
          <div style={{ marginTop: 16, fontSize: 22, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif' }}>Thank you!</div>
          <div style={{ marginTop: 8, fontSize: 15, color: '#666', fontFamily: 'sans-serif', lineHeight: 1.6 }}>
            Your testimonial has been submitted and is pending review by {advisor.name}.
            It will appear on their profile once approved.
          </div>
          <Link to="/" style={{ display: 'inline-block', marginTop: 24, color: G, fontSize: 14, fontFamily: 'sans-serif', fontWeight: 600, textDecoration: 'none' }}>← Back to Viysr</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: BG }}>
      <nav style={{ background: '#fff', borderBottom: `1px solid ${BORDER}`, padding: '0 32px', height: 60, display: 'flex', alignItems: 'center' }}>
        <LogoFull />
      </nav>

      <div style={{ maxWidth: 540, margin: '48px auto', padding: '0 24px' }}>
        <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 20, padding: '36px 36px 32px' }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: G, fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
              Testimonial for
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 4 }}>
              {advisor.name}
            </div>
            {advisor.title && (
              <div style={{ fontSize: 14, color: '#888', fontFamily: 'sans-serif' }}>{advisor.title}</div>
            )}
          </div>

          {error && (
            <div style={{ background: RED_BG, border: `1.5px solid #f5c6c2`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: RED, fontFamily: 'sans-serif' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: DARK, fontFamily: 'sans-serif', marginBottom: 6 }}>Your name *</label>
              <input
                type="text"
                value={form.author_name}
                onChange={e => setForm(f => ({ ...f, author_name: e.target.value }))}
                placeholder="Jane Smith"
                maxLength={100}
                style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 15, fontFamily: 'sans-serif', color: DARK, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: DARK, fontFamily: 'sans-serif', marginBottom: 6 }}>
                Company <span style={{ fontWeight: 400, color: '#aaa' }}>(optional)</span>
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                placeholder="Acme Corp"
                maxLength={100}
                style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 15, fontFamily: 'sans-serif', color: DARK, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: DARK, fontFamily: 'sans-serif', marginBottom: 6 }}>
                Your testimonial * <span style={{ fontWeight: 400, color: '#aaa' }}>({form.text.length}/1000)</span>
              </label>
              <textarea
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                placeholder={`Share your experience working with ${advisor.name}…`}
                maxLength={1000}
                rows={5}
                style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 15, fontFamily: 'sans-serif', color: DARK, boxSizing: 'border-box', resize: 'vertical', outline: 'none', lineHeight: 1.6 }}
              />
            </div>

            {SITEKEY && (
              <div style={{ marginBottom: 20 }}>
                <div ref={turnstileRef} />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '14px 24px', borderRadius: 999, fontSize: 15, fontFamily: 'sans-serif', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? 'Submitting…' : 'Submit testimonial'}
            </button>

            <div style={{ marginTop: 16, fontSize: 12, color: '#bbb', fontFamily: 'sans-serif', textAlign: 'center' }}>
              Your testimonial will be reviewed before appearing on the public profile.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
